import { useNavigate } from "@solidjs/router";
import { IoHomeOutline, IoSearchOutline } from "solid-icons/io";
import { type Component, createMemo, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Container, ContainerHeader } from "@/components/shared/Container";
import { Button } from "@/components/ui/button";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";

/**
 * Il corpo della pagina "non trovato", **senza** layout né titolo di scheda.
 *
 * Estratto da `NotFoundRoute` perché la rotta pubblica del profilo ha già il
 * proprio `RootLayout` e il proprio `<Title>`: riusare la rotta intera
 * annidererebbe due layout. Qui vive solo il blocco visivo.
 *
 * I default riproducono esattamente la 404 globale, quindi `NotFoundRoute` non
 * cambia comportamento: chi può pubblicare vede ancora la scorciatoia "Crea
 * annuncio".
 */
export const NotFoundView: Component<{
  /** Default: `404.title`. */
  title?: string;
  /** Default: `404.description`. */
  description?: string;
  /**
   * Scorciatoia "Crea annuncio" per chi ha `can_publish`. Spenta sulla 404 del
   * profilo pubblico: chi arriva lì da un link condiviso fuori piattaforma sta
   * cercando una persona, e proporgli di pubblicare un annuncio è fuori tema
   * (D10). Il bottone "Torna alla home" resta in entrambi i casi.
   */
  showPublisherCta?: boolean;
}> = (props) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [authStore] = useAuthV2();

  const redirectPath = createMemo(() => {
    if (authStore?.capabilities?.can_publish) {
      return "/profile/company";
    }
    return "/";
  });

  const showPublisherCta = createMemo(
    () =>
      props.showPublisherCta !== false &&
      !!authStore?.capabilities?.can_publish,
  );

  return (
    <div class="min-h-screen w-full bg-white">
      <ContainerHeader>
        <Container class="flex flex-col items-center justify-center text-center py-16">
          <div class="max-w-md mx-auto space-y-6">
            <div class="text-8xl font-bold text-primary/20">404</div>

            <h1 class="text-4xl font-bold text-gray-900">
              {props.title ?? t("404.title")}
            </h1>

            <p class="text-lg text-gray-600">
              {props.description ?? t("404.description")}
            </p>

            <div class="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button
                onClick={() => navigate(redirectPath())}
                class="flex items-center gap-2"
              >
                <IoHomeOutline class="w-5 h-5" />
                {t("404.button")}
              </Button>
              <Show when={showPublisherCta()}>
                <Button
                  variant="outline"
                  onClick={() => navigate("/job-offers/create/ai")}
                  class="flex items-center gap-2"
                >
                  <IoSearchOutline class="w-5 h-5" />
                  {t("404.createJobOffer")}
                </Button>
              </Show>
            </div>
          </div>
        </Container>
      </ContainerHeader>
    </div>
  );
};
